import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Search, X, UserRound } from "lucide-react";

const empty = {
  full_name: "", email: "", phone: "", nationality: "", fei_number: "", notes: "", portal_access: false,
  portal_login_email: "", portal_password: "", portal_password_confirm: "", portal_force_password_change: false,
};

export default function Owners() {
  const navigate = useNavigate();
  const [owners, setOwners] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(empty);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/owners");
      setOwners(data);
    } catch (e) {
      setError(formatApiError(e));
      setOwners([]);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddError("");
    try {
      if (form.portal_access) {
        if (!form.portal_login_email) {
          setAddError("Login email is required when portal access is enabled.");
          setAdding(false); return;
        }
        if ((form.portal_password || "").length < 8) {
          setAddError("Password must be at least 8 characters.");
          setAdding(false); return;
        }
        if (form.portal_password !== form.portal_password_confirm) {
          setAddError("Passwords do not match.");
          setAdding(false); return;
        }
      }
      const payload = {
        full_name: form.full_name,
        portal_access: form.portal_access,
      };
      ["email", "phone", "nationality", "fei_number", "notes"].forEach((k) => {
        if (form[k] !== "" && form[k] != null) payload[k] = form[k];
      });
      if (form.portal_access) {
        payload.portal_login_email = form.portal_login_email.trim().toLowerCase();
        payload.portal_password = form.portal_password;
        payload.portal_force_password_change = !!form.portal_force_password_change;
      }
      await api.post("/owners", payload);
      setShowAdd(false);
      setForm(empty);
      await load();
    } catch (err) {
      setAddError(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const filtered = (owners || []).filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.full_name || "").toLowerCase().includes(q) ||
      (o.email || "").toLowerCase().includes(q) ||
      (o.nationality || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="owners-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="se-label">Clients</div>
          <h1 className="font-heading mt-1" style={{ fontSize: 28 }}>Owners</h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            People who own horses boarded at your stable.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8C9E8A" }} />
            <input
              className="se-input"
              style={{ paddingLeft: 36, minWidth: 240 }}
              placeholder="Search name, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="owners-search-input"
            />
          </div>
          <button className="se-btn" onClick={() => setShowAdd(true)} data-testid="owners-add-button">
            <Plus size={16} /> Add owner
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 text-sm px-3 py-2 rounded-lg"
          style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
          data-testid="owners-error"
        >
          {error}
        </div>
      )}

      <div className="se-card p-0 overflow-hidden" data-testid="owners-table-card">
        <div className="overflow-x-auto">
          <table className="se-table w-full" data-testid="owners-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Nationality</th>
                <th>Portal</th>
              </tr>
            </thead>
            <tbody>
              {owners === null && (
                <tr><td colSpan={5} style={{ color: "#8C9E8A" }}>Loading…</td></tr>
              )}
              {owners !== null && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: "#8C9E8A" }} data-testid="owners-empty">
                    No owners yet. Click "Add owner" to register your first client.
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/owners/${o.id}`)}
                  style={{ cursor: "pointer" }}
                  data-testid={`owner-row-${o.id}`}
                >
                  <td style={{ fontWeight: 600 }}>
                    <span className="inline-flex items-center gap-2">
                      <UserRound size={14} style={{ color: "#8C9E8A" }} />
                      {o.full_name}
                    </span>
                  </td>
                  <td>{o.email || "—"}</td>
                  <td>{o.phone || "—"}</td>
                  <td>{o.nationality || "—"}</td>
                  <td>
                    <span className={`se-badge ${o.portal_access ? "se-badge-active" : "se-badge-inactive"}`}>
                      {o.portal_access ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center p-4"
          style={{ background: "rgba(59,47,30,0.5)" }}
          onClick={() => setShowAdd(false)}
          data-testid="owner-modal-backdrop"
        >
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="se-card w-full max-w-lg"
            data-testid="owner-add-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading" style={{ fontSize: 18 }}>Add a new owner</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="se-btn se-btn-ghost" data-testid="owner-modal-close">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="se-label block mb-1">Full name</label>
                <input className="se-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="owner-form-name" />
              </div>
              <div>
                <label className="se-label block mb-1">Email</label>
                <input className="se-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="owner-form-email" />
              </div>
              <div>
                <label className="se-label block mb-1">Phone</label>
                <input className="se-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="owner-form-phone" />
              </div>
              <div>
                <label className="se-label block mb-1">Nationality</label>
                <input className="se-input" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="owner-form-nationality" />
              </div>
              <div>
                <label className="se-label block mb-1">FEI #</label>
                <input className="se-input" value={form.fei_number} onChange={(e) => setForm({ ...form, fei_number: e.target.value })} placeholder="Optional" data-testid="owner-form-fei-number" />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="owner-form-portal-label">
                  <input
                    type="checkbox"
                    checked={form.portal_access}
                    onChange={(e) => setForm({
                      ...form,
                      portal_access: e.target.checked,
                      portal_login_email: e.target.checked && !form.portal_login_email ? (form.email || "") : form.portal_login_email,
                    })}
                    data-testid="owner-form-portal-checkbox"
                  />
                  Grant portal login access
                </label>
              </div>
              {form.portal_access && (
                <div
                  className="col-span-2 p-4 rounded-lg space-y-3"
                  style={{ background: "rgba(45,110,110,0.06)", border: "0.5px solid rgba(45,110,110,0.2)" }}
                  data-testid="owner-form-portal-section"
                >
                  <div className="se-label">Portal login</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <label className="se-label block mb-1">Login email *</label>
                      <input className="se-input" type="email" required value={form.portal_login_email}
                        onChange={(e) => setForm({ ...form, portal_login_email: e.target.value })}
                        data-testid="owner-form-portal-email" />
                    </div>
                    <div>
                      <label className="se-label block mb-1">Password *</label>
                      <input className="se-input" type="password" required minLength={8} value={form.portal_password}
                        onChange={(e) => setForm({ ...form, portal_password: e.target.value })}
                        placeholder="Min 8 characters" autoComplete="new-password"
                        data-testid="owner-form-portal-password" />
                    </div>
                    <div>
                      <label className="se-label block mb-1">Confirm password *</label>
                      <input className="se-input" type="password" required minLength={8} value={form.portal_password_confirm}
                        onChange={(e) => setForm({ ...form, portal_password_confirm: e.target.value })}
                        autoComplete="new-password"
                        data-testid="owner-form-portal-password-confirm" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.portal_force_password_change}
                      onChange={(e) => setForm({ ...form, portal_force_password_change: e.target.checked })}
                      data-testid="owner-form-portal-force" />
                    Require password change at next login
                  </label>
                  <p className="text-xs" style={{ color: "#8C9E8A" }}>
                    No email will be sent. Share these credentials with the owner directly.
                  </p>
                </div>
              )}
              <div className="col-span-2">
                <label className="se-label block mb-1">Notes</label>
                <textarea
                  className="se-input"
                  style={{ height: 80, padding: "10px 14px" }}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="owner-form-notes"
                />
              </div>
            </div>

            {addError && (
              <div
                className="mt-4 text-sm px-3 py-2 rounded-lg"
                style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
                data-testid="owner-form-error"
              >
                {addError}
              </div>
            )}

            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setShowAdd(false)} data-testid="owner-form-cancel">
                Cancel
              </button>
              <button type="submit" className="se-btn" disabled={adding} data-testid="owner-form-submit">
                {adding ? "Adding…" : "Add owner"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
